import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { fmtEur } from '../../utils/formatArcaNumbers';
import { Button } from '../ui/button';

interface OneTimeContributionSheetProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (amount: number) => Promise<void>;
	/** Importe mínimo (€) aceptado para una aportación puntual. */
	minAmount?: number;
	/**
	 * Margen de tope fiscal que le queda al usuario = importe MÁXIMO. Lo calcula
	 * el backend con el mismo código con el que después valida, así que lo que se
	 * pinta aquí es exactamente lo que se va a aceptar.
	 */
	maxAmount?: number;
	/** El margen todavía se está pidiendo al backend. */
	loadingLimits?: boolean;
	/**
	 * ¿Puede aportar HOY? Es el guard de "máximo una al día" del backend,
	 * adelantado por `GET /limits`.
	 *
	 * `undefined` significa "no lo sabemos todavía" (o la llamada falló) y NO
	 * bloquea: el backend es quien manda y rechazará si hace falta. Bloquear a
	 * ciegas impediría aportar a quien sí puede.
	 */
	canContributeToday?: boolean;
	translate: (key: string) => string;
}

// Mínimo por defecto mientras el backend responde con el real.
const DEFAULT_MIN_AMOUNT = 10;

const OneTimeContributionSheet: React.FC<OneTimeContributionSheetProps> = ({
	open,
	onClose,
	onConfirm,
	minAmount = DEFAULT_MIN_AMOUNT,
	maxAmount,
	loadingLimits = false,
	canContributeToday,
	translate
}) => {
	const [amount, setAmount] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (open) {
			setAmount('');
		}
	}, [open]);

	// Si lo que le queda no llega al mínimo no hay importe posible: se le dice,
	// en vez de dejarle con el botón deshabilitado para siempre y sin explicación.
	const hasNoMarginLeft = maxAmount !== undefined && maxAmount < minAmount;

	// Ya ha gastado su aportación de hoy. Solo bloquea con un `false` explícito
	// del backend: `undefined` es "no lo sabemos" y no puede cerrar la puerta.
	const alreadyContributedToday = canContributeToday === false;

	const numericAmount = Number(amount);
	// Sin margen conocido no se limita por arriba: el backend es quien manda, y
	// bloquear a ciegas impediría aportar a quien sí puede.
	const fitsInRemaining = maxAmount === undefined || numericAmount <= maxAmount;
	const isValid =
		amount !== '' &&
		Number.isFinite(numericAmount) &&
		numericAmount >= minAmount &&
		fitsInRemaining &&
		!alreadyContributedToday &&
		!loadingLimits;

	const handleConfirm = async () => {
		// `loading` va aquí y no en `isValid` a propósito: el botón ya se
		// deshabilita solo, pero el Enter del input NO pasa por el `disabled`, así
		// que sin esta guarda dos pulsaciones seguidas mandan dos POST concurrentes
		// y el "máximo una al día" del backend no llega a tiempo — doble cobro.
		if (!isValid || loading) return;
		setLoading(true);
		try {
			await onConfirm(numericAmount);
			onClose();
		} catch {
			// El caller maneja el toast/feedback de error. El modal se queda abierto
			// para que el usuario pueda reintentar o cancelar.
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-md">
				<DialogHeader className="pb-2">
					<DialogTitle className="text-xl">
						{translate('individual.oneTimeContribution.title') || 'Aportación puntual'}
					</DialogTitle>
					<DialogDescription className="text-sm">
						{translate('individual.oneTimeContribution.subtitle') ||
							'Aporta una cantidad única a tu plan, además de tu aportación mensual.'}
					</DialogDescription>
				</DialogHeader>

				<div className="py-3">
					<label
						htmlFor="one-time-amount"
						className="block text-sm font-medium text-foreground mb-1.5"
					>
						{translate('individual.oneTimeContribution.amountLabel') || 'Importe'}
					</label>
					<div className="relative">
						<input
							id="one-time-amount"
							type="number"
							inputMode="decimal"
							min={minAmount}
							max={maxAmount}
							step={5}
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							onKeyDown={(e) => {
								// Teclear el importe y pulsar Enter es el gesto natural aquí.
								// `handleConfirm` revalida por su cuenta: este camino no pasa
								// por el `disabled` del botón.
								if (e.key === 'Enter') {
									handleConfirm();
								}
							}}
							placeholder={String(minAmount)}
							className="input-arca w-full text-base pr-8"
						/>
						<span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
							€
						</span>
					</div>
					<p className="text-xs text-muted-foreground pt-2 leading-relaxed">
						{(
							translate('individual.oneTimeContribution.limitNote') ||
							'Se suma a tu límite fiscal anual. Mínimo {min}€.'
						).replace('{min}', String(minAmount))}
					</p>
					{maxAmount !== undefined && !hasNoMarginLeft && (
						<p
							data-testid="one-time-remaining"
							className="text-xs text-muted-foreground pt-1 leading-relaxed"
						>
							{(
								translate('individual.oneTimeContribution.remainingNote') ||
								'Este año puedes aportar hasta {max} más.'
							).replace('{max}', fmtEur(maxAmount))}
						</p>
					)}
					{hasNoMarginLeft && (
						<p
							data-testid="one-time-no-margin"
							className="text-xs text-destructive pt-1 leading-relaxed"
						>
							{translate('individual.oneTimeContribution.errorNoMarginLeft') ||
								'Ya has agotado tu límite de aportación de este año.'}
						</p>
					)}
					{alreadyContributedToday && (
						<p
							data-testid="one-time-already-today"
							// El aviso aparece cuando resuelve `/limits`, con el modal ya
							// abierto: sin región viva, un lector de pantalla no lo anuncia y
							// el botón se queda deshabilitado sin motivo audible.
							role="status"
							aria-live="polite"
							className="text-xs text-destructive pt-1 leading-relaxed"
						>
							{translate('individual.oneTimeContribution.errorAlreadyToday') ||
								'Hoy ya hay una aportación a tu plan. Puedes hacer otra a partir de mañana.'}
						</p>
					)}
				</div>

				<div className="flex gap-3 pt-2">
					<Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>
						{translate('individual.oneTimeContribution.cancel') || 'Cancelar'}
					</Button>
					<Button className="flex-1" onClick={handleConfirm} disabled={loading || !isValid}>
						{loading ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : (
							translate('individual.oneTimeContribution.confirm') || 'Aportar'
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default OneTimeContributionSheet;
